<script setup>
import { computed, useCssModule } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty } from '@utils';
import { XIcon } from '@clicksign/excalibur';

const props = defineProps({
  validDocument: {
    type: Object,
    default: () => ({}),
  },
  borderBottom: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const $style = useCssModule();

const customClass = computed(() => ({
  [$style.borderBottom]: props.borderBottom,
}));
</script>

<template>
  <div :class="[$style.certifyInfo, customClass]">
    <div :class="$style.documentInfo">
      <h4 :class="$style.documentName">
        {{ validDocument.name }}
      </h4>

      <small
        v-if="!isEmpty(validDocument.file_hash)"
        :class="$style.hashNumber"
      >
        {{ t('validator.document.result.file_hash') }} {{ validDocument.file_hash }}
      </small>
    </div>

    <ul :class="$style.list">
      <li
        v-if="validDocument.signed_by_clicksign"
        :class="[$style.listItem, $style.signatureInfo]"
      >
        <XIcon
          :class="$style.listItemIcon"
          icon="check-circle"
          size="lg"
          left
        />

        <p :class="$style.listItemText">
          {{ t('validator.document.result.signed') }}
        </p>
      </li>

      <li
        v-if="validDocument.signed_by_clicksign"
        :class="[$style.listItem, $style.certifyDetail]"
      >
        <XIcon
          :class="$style.listItemIcon"
          icon="shield-check"
          size="lg"
          left
        />

        <p :class="$style.listItemText">
          {{ t('validator.document.result.icp_valid') }}
        </p>
      </li>

      <li
        v-if="!validDocument.signed_by_clicksign"
        :class="[$style.listItem, $style.errorInfo]"
      >
        <XIcon
          :class="$style.listItemIcon"
          icon="exclamation-triangle"
          size="lg"
          left
        />

        <p :class="$style.listItemText">
          {{ t('validator.document.result.invalid') }}
        </p>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" module>
.certifyInfo {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  padding: var(--space-regular) 0 0;
  gap: var(--space-medium);
  border-top: var(--border-width-small) solid var(--color-neutral-400);

  &.borderBottom {
    padding: var(--space-regular) 0;
    border-bottom: var(--border-width-small) solid var(--color-neutral-400);
  }

  .documentInfo {
    .documentName {
      margin-bottom: 0px;
      font-size: var(--font-size-medium);
      font-weight: var(--font-weight-bold);
    }

    .hashNumber {
      color: var(--color-neutral-700);
    }
  }

  .list {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    padding: 0px;
    gap: var(--space-small-xx);
    list-style: none;

    .listItem {
      display: flex;
      align-items: center;

      &.signatureInfo {
        .listItemIcon {
          color: var(--color-feedback-success-400);
        }
      }

      &.certifyDetail {
        .listItemIcon {
          color: var(--color-feedback-info-400);
        }
      }

      &.errorInfo {
        color: var(--color-feedback-error-400);
      }
    }

    .listItemText {
      margin: 0;
      font-size: var(--font-size-regular);
      color: var(--color-neutral-1000);
    }
  }
}
</style>
